<template>
    <div class="bl-table-card">
        <div class="table-responsive">
            <table class="table bl-table">
                <thead>
                <tr>
                    <th>Master Budget Line</th>
                    <th>Create Date</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Interval</th>
                    <th class="text-center">Status</th>
                    <th class="text-end">Action</th>
                </tr>
                </thead>
                <tbody v-if="loading">
                <TableSkeleton :rows="6" :columns="7"/>
                </tbody>
                <tbody v-if="! loading">
                <tr v-for="item in rows" :key="item.id">
                    <td>
                        <button type="button" class="bl-name" @click="emit('edit', item.id)">{{ item.name }}</button>
                        <div v-if="item.code" class="bl-code">{{ item.code }}</div>
                    </td>
                    <td class="text-nowrap">{{ displayDate(item.date) }}</td>
                    <td class="text-nowrap">{{ displayDate(item.start_date) }}</td>
                    <td class="text-nowrap">{{ displayDate(item.end_date) }}</td>
                    <td class="text-capitalize">{{ humanize(item.interval) }}</td>
                    <td class="text-center">
                        <StatusChip :status="item.status"/>
                    </td>
                    <td class="text-end">
                        <button type="button" class="bl-action-btn" title="Edit master budget line" @click="emit('edit', item.id)">
                            <i class="fa-regular fa-pen-to-square"></i>
                            <span>Edit</span>
                        </button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <EmptyState
            v-if="! loading && ! rows.length"
            icon="fa-solid fa-layer-group"
            title="No master budget lines found"
            message="Try a different date range or filter, or add a new master budget line."
        >
            <button type="button" class="btn bl-empty-btn" @click="emit('add')">
                <i class="fa-solid fa-plus"></i>
                <span>Add Master Budget Line</span>
            </button>
        </EmptyState>
    </div>
</template>

<script setup>
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import StatusChip from '@/components/atom/StatusChip.vue'
import EmptyState from '@/components/atom/EmptyState.vue'
import TableSkeleton from '@/components/atom/TableSkeleton.vue'

defineProps({
    rows: {
        type: Array,
        default: () => []
    },
    loading: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['edit', 'add'])

const humanize = (value) => {
    if (! value) return '—'
    return String(value).replace(/_/g, ' ')
}

const displayDate = (value) => {
    if (! value) return '—'
    const parsed = parseISO(value)
    if (isNaN(parsed.getTime())) return value
    return format(parsed, 'dd MMM yyyy')
}
</script>

<style scoped>
.bl-table-card {
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    overflow: hidden;
}
.bl-table {
    min-width: 760px;
    margin-bottom: 0;
}
.bl-table thead th {
    padding: 12px 14px;
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #475467;
    white-space: nowrap;
}
.bl-table tbody td {
    padding: 14px;
    border-bottom: 1px solid #f2f4f7;
    font-size: 14px;
    line-height: 1.45;
    color: #344054;
    vertical-align: middle;
}
.bl-table tbody tr:last-child td {
    border-bottom: 0;
}
.bl-table tbody tr:hover {
    background: #fcfcfd;
}
.bl-name {
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 14px;
    font-weight: 600;
    color: #101828;
    text-align: left;
    cursor: pointer;
}
.bl-name:hover {
    color: #7367f0;
}
.bl-code {
    margin-top: 2px;
    font-size: 13px;
    color: #667085;
}
.bl-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid #e4e7ec;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    color: #475467;
    transition: all .15s ease;
}
.bl-action-btn:hover {
    border-color: #7367f0;
    background: #f4f3ff;
    color: #7367f0;
}
.bl-empty-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 16px;
    border-radius: 8px;
    background: #7367f0;
    border: 1px solid #7367f0;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
}
.bl-empty-btn:hover {
    background: #5e50ee;
    border-color: #5e50ee;
    color: #fff;
}
</style>
